import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ProductoApiService } from '../services/producto-api.service';
import { ProductBundleApiService } from '../services/product-bundle-api.service';
import { ProductResponse, CurrencyResponse } from '../models/producto.model';
import {
  ProductBundleResponse,
  ProductBundleRequest,
  ProductBundleItemResponse,
  ProductBundleItemRequest,
} from '../models/product-bundle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-product-bundle-form',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './product-bundle-form.component.html',
})
export class ProductBundleFormComponent implements OnInit {
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<ProductBundleResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  bundleId: number | null = null;
  businessCurrency: number | null | undefined = null;
  currentBundle: ProductBundleResponse | null = null;

  monedas: CurrencyResponse[] = [];

  bundleItems: ProductBundleItemResponse[] = [];
  loadingItems = false;

  showItemForm = false;
  editingItemId: number | null = null;
  itemForm!: FormGroup;
  savingItem = false;

  productSearch = '';
  productResults: ProductResponse[] = [];
  searchingProducts = false;
  selectedProduct: ProductResponse | null = null;

  minDatetimeLocal = '';

  private businessId!: number;
  private productsById = new Map<number, ProductResponse>();

  constructor(
    private fb: FormBuilder,
    private productApi: ProductoApiService,
    private bundleApi: ProductBundleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;

    this.initForm();
    this.initItemForm();
    this.cargarMonedas();
    this.updateMinDate();

    if (!this.drawerMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditing = true;
        this.bundleId = +id;
        this.cargarBundle(this.bundleId);
      }
    }
  }

  private updateMinDate(): void {
    this.minDatetimeLocal = this.toLocalDateInput(new Date());
  }

  private toLocalDateInput(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  reset(): void {
    this.isEditing = false;
    this.bundleId = null;
    this.currentBundle = null;
    this.bundleItems = [];
    this.error = '';
    this.saving = false;
    this.showItemForm = false;
    this.editingItemId = null;
    this.initForm();
    this.initItemForm();
  }

  loadProductBundle(id: number): void {
    this.isEditing = true;
    this.bundleId = id;
    this.cargarBundle(id);
  }

  private initForm(): void {
    this.businessCurrency =
      this.authService.getCurrentBusiness()?.defaultCurrencyId;
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      sku: ['', Validators.maxLength(100)],
      description: ['', Validators.maxLength(500)],
      bundlePrice: [null, Validators.min(0)],
      discountPercent: [null, [Validators.min(0), Validators.max(100)]],
      currencyId: [this.businessCurrency],
      imageUrl: ['', Validators.maxLength(500)],
      priceStrategy: ['fixed'],
      displayMode: ['single_line'],
      bundleRole: ['standard'],
      // Vigencia promocional (Incidentes #33 y #56)
      promoValidFrom: [null],
      promoValidUntil: [null],
      promoPrice: [null, Validators.min(0.01)],
      promoEnabled: [true],
      isActive: [true],
    });
  }

  /** Inc 56: precio promocional capturado (null si el campo está vacío). */
  get promoPriceValue(): number | null {
    const raw = this.form?.get('promoPrice')?.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }

  /** Inc 56: debe ser > 0 y menor que el precio del paquete (el backend responde 400). */
  get promoPriceInvalid(): boolean {
    const promo = this.promoPriceValue;
    if (promo === null) return false;
    if (promo <= 0) return true;
    const price = Number(this.form?.get('bundlePrice')?.value);
    if (isNaN(price) || !price) return false;
    return promo >= price;
  }

  /** Inc 56: con precio promocional, ambas fechas son obligatorias. */
  get promoDatesRequiredMissing(): boolean {
    if (this.promoPriceValue === null) return false;
    return (
      !this.form?.get('promoValidFrom')?.value ||
      !this.form?.get('promoValidUntil')?.value
    );
  }

  /** ¿Hay alguna fecha de vigencia promocional capturada? */
  get tieneVigenciaPromo(): boolean {
    return !!(
      this.form?.get('promoValidFrom')?.value ||
      this.form?.get('promoValidUntil')?.value
    );
  }

  /**
   * Vigencia cargada pero sin precio promocional: promoción a medias. El backend
   * la rechaza con 400 — una vigencia sin precio no promociona nada.
   */
  get promoPriceRequiredMissing(): boolean {
    return this.tieneVigenciaPromo && this.promoPriceValue === null;
  }

  /**
   * Motivo por el que la promoción no se puede guardar, o null si está bien.
   * El backend rechaza los mismos cuatro casos con 400.
   */
  get errorPromocionMessage(): string | null {
    if (this.promoRangeInvalid) {
      return 'La fecha de fin de vigencia no puede ser anterior a la de inicio.';
    }
    if (this.promoPriceInvalid) {
      return 'El precio promocional debe ser mayor que 0 y menor que el precio del paquete.';
    }
    if (this.promoDatesRequiredMissing) {
      return 'Para definir un precio promocional debes indicar la fecha de inicio y la de fin de vigencia.';
    }
    if (this.promoPriceRequiredMissing) {
      return 'Para definir una vigencia promocional debes indicar el precio promocional.';
    }
    return null;
  }

  /**
   * Quitar la promoción borra TODA la promoción: dejar el precio sin fechas (o al
   * revés) es el estado a medias que el backend rechaza con 400.
   */
  removePromotion(): void {
    this.form.patchValue({
      promoValidFrom: '',
      promoValidUntil: '',
      promoPrice: null,
    });
    this.form.markAsDirty();
  }

  /** Inc 56: estado autoritativo del backend (4 valores exactos). */
  get promoStatusLabel(): string {
    switch (this.currentBundle?.promoStatus) {
      case 'active':
        return 'Activa';
      case 'scheduled':
        return 'Programada';
      case 'expired':
        return 'Expirada';
      default:
        return 'Inactiva';
    }
  }

  get promoStatusClass(): string {
    switch (this.currentBundle?.promoStatus) {
      case 'active':
        return 'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-300';
      case 'scheduled':
        return 'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-300';
      case 'expired':
        return 'bg-error-100 text-error-700 dark:bg-error-500/20 dark:text-error-300';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  }

  /**
   * Vigencia promocional inválida: fin anterior a inicio (Incidente #33).
   * Solo aplica cuando ambas fechas están presentes.
   */
  get promoRangeInvalid(): boolean {
    const from = this.form?.get('promoValidFrom')?.value;
    const until = this.form?.get('promoValidUntil')?.value;
    return !!from && !!until && new Date(until) < new Date(from);
  }

  private initItemForm(): void {
    this.itemForm = this.fb.group({
      idProduct: [null, Validators.required],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      displayOrder: [0],
      isOptional: [false],
    });
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => (this.monedas = data),
      error: () => {},
    });
  }

  cargarBundle(id: number): void {
    this.loading = true;
    this.bundleApi.getProductBundleById(id, this.businessId).subscribe({
      next: (bundle) => {
        this.currentBundle = bundle;
        this.form.patchValue({
          name: bundle.name,
          sku: bundle.sku,
          description: bundle.description || '',
          bundlePrice: bundle.bundlePrice ?? null,
          discountPercent: bundle.discountPercent ?? null,
          currencyId: bundle.currencyId || null,
          imageUrl: bundle.imageUrl || '',
          priceStrategy: bundle.priceStrategy || 'fixed',
          displayMode: bundle.displayMode || 'single_line',
          bundleRole: bundle.bundleRole || 'standard',
          promoValidFrom: bundle.promoValidFrom ?? null,
          promoValidUntil: bundle.promoValidUntil ?? null,
          promoPrice: bundle.promoPrice ?? null,
          promoEnabled: bundle.promoEnabled ?? true,
          isActive: bundle.isActive,
        });
        this.loading = false;
        this.cargarItems();
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el bundle';
        this.loading = false;
      },
    });
  }

  private cargarItems(): void {
    if (!this.bundleId) return;
    this.loadingItems = true;
    this.bundleApi.getBundleItems(this.bundleId, this.businessId).subscribe({
      next: (items) => {
        this.bundleItems = items;
        this.loadingItems = false;
        this.cargarDatosProductosDeItems();
      },
      error: () => {
        this.bundleItems = [];
        this.loadingItems = false;
      },
    });
  }

  private cargarDatosProductosDeItems(): void {
    const idsUnicos = [...new Set(this.bundleItems.map((it) => it.idProduct))];
    const idsPorCargar = idsUnicos.filter((id) => !this.productsById.has(id));

    if (idsPorCargar.length === 0) return;

    idsPorCargar.forEach((id) => {
      this.productApi.getProductById(id, this.businessId).subscribe({
        next: (product) => {
          this.productsById.set(id, product);
        },
        error: () => {},
      });
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Paquete' : 'Nuevo Paquete';
  }

  get isSumOfComponents(): boolean {
    return this.form.get('priceStrategy')?.value === 'sum_of_components';
  }

  getProductName(idProduct: number): string {
    return (
      this.productsById.get(idProduct)?.name ?? `Producto ID: ${idProduct}`
    );
  }

  getProductSku(idProduct: number): string {
    return this.productsById.get(idProduct)?.sku ?? '';
  }

  getProductPrice(idProduct: number): number | undefined {
    return this.productsById.get(idProduct)?.price;
  }

  guardar(): void {
    const errorPromo = this.errorPromocionMessage;
    if (errorPromo) {
      this.form.markAllAsTouched();
      this.toast.error({ title: 'Promoción', message: errorPromo });
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Lista de precios',
        message:
          'No se realizaron nuevos cambios en la regla de lista de precios',
      });
      this.router.navigate(['/bundles']);
      return;
    }

    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const request: ProductBundleRequest = {
      name: v.name,
      sku: v.sku,
      description: v.description || undefined,
      bundlePrice:
        v.priceStrategy === 'sum_of_components'
          ? this.precioCalculadoLocal
          : (v.bundlePrice ?? undefined),
      discountPercent: v.discountPercent ?? undefined,
      currencyId: v.currencyId || undefined,
      imageUrl: v.imageUrl || undefined,
      priceStrategy: v.priceStrategy || undefined,
      displayMode: v.displayMode || undefined,
      bundleRole: v.bundleRole || undefined,
      // Vigencia promocional (Incidentes #33 y #56)
      promoValidFrom: v.promoValidFrom || undefined,
      promoValidUntil: v.promoValidUntil || undefined,
      promoPrice: this.promoPriceValue ?? undefined,
      promoEnabled: v.promoEnabled !== false,
      isActive: v.isActive,
      businessId: this.businessId,
    };

    const request$ =
      this.isEditing && this.bundleId
        ? this.bundleApi.updateProductBundle(
            this.bundleId,
            this.businessId,
            request,
          )
        : this.bundleApi.createProductBundle(request);

    request$.subscribe({
      next: (bundle) => {
        // Si era CREACIÓN y hay items locales, persistirlos ahora
        if (!this.isEditing && this.bundleItems.length > 0) {
          this.persistirItemsLocales(bundle);
        } else {
          // No hay items que persistir, terminamos
          this.finalizarGuardado(bundle);
        }
      },
      error: (err) => {
        this.saving = false;
        this.error =
          err.error?.message ||
          (this.isEditing ? 'Error al actualizar' : 'Error al crear');
        // Inc 42: con estrategia "suma de componentes" el backend valida que todos
        // los componentes tengan precio → 400 con mensaje claro.
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  private itemPersistError = '';

  private persistirItemsLocales(bundle: ProductBundleResponse): void {
    const itemsAGuardar = [...this.bundleItems];
    let exitosos = 0;
    let fallidos = 0;
    let completados = 0;
    const total = itemsAGuardar.length;
    this.itemPersistError = '';

    itemsAGuardar.forEach((item) => {
      const req: ProductBundleItemRequest = {
        idProduct: item.idProduct,
        quantity: item.quantity,
        displayOrder: item.displayOrder,
        isOptional: item.isOptional,
      };

      this.bundleApi
        .createBundleItem(bundle.id, this.businessId, req)
        .subscribe({
          next: () => {
            exitosos++;
            completados++;
            this.checkPersistenciaCompleta(
              bundle,
              exitosos,
              fallidos,
              total,
              completados,
            );
          },
          error: (err) => {
            fallidos++;
            completados++;
            // Inc 42: conserva el mensaje del backend (p. ej. "El producto no tiene precio configurado ...").
            if (err.error?.message) this.itemPersistError = err.error.message;
            this.checkPersistenciaCompleta(
              bundle,
              exitosos,
              fallidos,
              total,
              completados,
            );
          },
        });
    });
  }

  private checkPersistenciaCompleta(
    bundle: ProductBundleResponse,
    exitosos: number,
    fallidos: number,
    total: number,
    completados: number,
  ): void {
    if (completados < total) return;

    this.saving = false;
    if (fallidos > 0) {
      this.toast.error({
        title: 'Paquete creado con advertencias',
        message: this.itemPersistError
          ? `${this.itemPersistError} (${fallidos} de ${total} productos no se agregaron).`
          : `Se creó el paquete pero ${fallidos} de ${total} items no se pudieron agregar. Puedes intentarlo nuevamente desde la edición.`,
      });
    } else {
      this.toast.success({
        title: 'Paquete creado',
        message: `El paquete se creó con ${exitosos} item(s).`,
      });
    }

    if (this.drawerMode) {
      this.saved.emit(bundle);
    } else {
      this.router.navigate(['/bundles']);
    }
  }

  private finalizarGuardado(bundle: ProductBundleResponse): void {
    this.saving = false;
    if (this.drawerMode) {
      this.saved.emit(bundle);
    } else {
      this.router.navigate(['/bundles']);
    }
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/bundles']);
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['min'])
      return `El valor mínimo es ${control.errors['min'].min}`;
    if (control.errors['max'])
      return `El valor máximo es ${control.errors['max'].max}`;
    return 'Campo inválido';
  }

  // ============================================================
  // Bundle Items management
  // ============================================================

  abrirFormItem(): void {
    this.editingItemId = null;
    this.selectedProduct = null;
    this.productSearch = '';
    this.productResults = [];
    this.itemForm.reset({
      idProduct: null,
      quantity: 1,
      displayOrder: 0,
      isOptional: false,
    });
    this.showItemForm = true;
  }

  abrirFormEditarItem(item: ProductBundleItemResponse): void {
    this.editingItemId = item.id;

    // Precargar el producto del item para que se vea su nombre/SKU/precio al reabrir
    const prod = this.productsById.get(item.idProduct);
    this.selectedProduct = prod ?? null;
    this.productSearch = prod?.name ?? this.getProductName(item.idProduct);
    this.productResults = [];

    this.itemForm.patchValue({
      idProduct: item.idProduct,
      quantity: item.quantity,
      displayOrder: item.displayOrder,
      isOptional: item.isOptional,
    });
    this.showItemForm = true;
  }

  cerrarFormItem(): void {
    this.showItemForm = false;
    this.editingItemId = null;
    this.selectedProduct = null;
    this.productSearch = '';
    this.productResults = [];
  }

  buscarProductos(): void {
    if (this.productSearch.length < 2) {
      this.productResults = [];
      return;
    }
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        searchName: this.productSearch,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (response) => {
          this.productResults = response.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  onFocusProducto(): void {
    if (this.productSearch.length >= 2) return;
    this.searchingProducts = true;
    this.productApi
      .searchProduct({
        businessId: this.businessId,
        page: 0,
        size: 10,
      })
      .subscribe({
        next: (response) => {
          this.productResults = response.content;
          this.searchingProducts = false;
        },
        error: () => {
          this.productResults = [];
          this.searchingProducts = false;
        },
      });
  }

  onBlurProducto(): void {
    setTimeout(() => {
      this.productResults = [];
    }, 200);
  }

  seleccionarProducto(product: ProductResponse): void {
    this.selectedProduct = product;
    this.itemForm.patchValue({ idProduct: product.id });
    this.productResults = [];
    this.productSearch = product.name;
  }

  guardarItem(): void {
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }

    const v = this.itemForm.value;

    // Modo CREACIÓN: guardar en array local (sin backend)
    if (!this.bundleId) {
      if (this.editingItemId !== null) {
        // Editar item local existente
        const idx = this.bundleItems.findIndex(
          (it) => it.id === this.editingItemId,
        );
        if (idx !== -1) {
          this.bundleItems[idx] = {
            ...this.bundleItems[idx],
            idProduct: v.idProduct,
            quantity: v.quantity,
            displayOrder: v.displayOrder ?? 0,
            isOptional: v.isOptional,
          };
        }
      } else {
        // Agregar nuevo item local
        // Usamos un ID negativo temporal para distinguirlos de IDs reales del backend
        const tempId = -(Date.now() + this.bundleItems.length);
        this.bundleItems = [
          ...this.bundleItems,
          {
            id: tempId,
            idBundle: 0,
            idProduct: v.idProduct,
            quantity: v.quantity,
            displayOrder: v.displayOrder ?? 0,
            isOptional: v.isOptional,
          },
        ];
      }
      // Si el producto seleccionado está en memoria, lo cacheamos para mostrar el nombre
      if (this.selectedProduct) {
        this.productsById.set(this.selectedProduct.id, this.selectedProduct);
      }
      this.cerrarFormItem();
      return;
    }

    // Modo EDICIÓN: guardar en backend (comportamiento original)
    this.savingItem = true;
    const request: ProductBundleItemRequest = {
      idProduct: v.idProduct,
      quantity: v.quantity,
      displayOrder: v.displayOrder ?? 0,
      isOptional: v.isOptional,
    };

    const request$ =
      this.editingItemId !== null && this.editingItemId > 0
        ? this.bundleApi.updateBundleItem(
            this.bundleId,
            this.editingItemId,
            this.businessId,
            request,
          )
        : this.bundleApi.createBundleItem(
            this.bundleId,
            this.businessId,
            request,
          );

    request$.subscribe({
      next: () => {
        this.savingItem = false;
        this.cerrarFormItem();
        this.cargarItems();
        // Inc 42: recarga el bundle para reflejar bundlePrice/calculatedPrice recalculados por el backend.
        if (this.bundleId) this.cargarBundle(this.bundleId);
      },
      error: (err) => {
        this.savingItem = false;
        this.error = err.error?.message || 'Error al guardar item';
        // Inc 42: 400 "El producto no tiene precio configurado ..." con estrategia suma de componentes.
        this.toast.error({
          title: 'No se pudo agregar el producto',
          message: this.error,
        });
      },
    });
  }

  async eliminarItem(item: ProductBundleItemResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar item',
      message: '¿Eliminar este item del paquete?',
    });
    if (!ok) return;

    // Modo CREACIÓN: eliminar del array local
    if (!this.bundleId) {
      this.bundleItems = this.bundleItems.filter((it) => it.id !== item.id);
      return;
    }

    // Modo EDICIÓN: eliminar en backend (comportamiento original)
    this.bundleApi
      .deleteBundleItem(this.bundleId, item.id, this.businessId)
      .subscribe({
        next: () => {
          this.cargarItems();
          // Inc 42: recalcula el precio del paquete al quitar un componente.
          if (this.bundleId) this.cargarBundle(this.bundleId);
          this.toast.deleted('Item del paquete');
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al eliminar item';
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error al eliminar item',
          });
        },
      });
  }

  formatPrecio(price?: number, currencyId?: number): string {
    if (price === undefined || price === null) return '-';
    const currency = this.monedas.find((m) => m.id === currencyId);
    if (!currency) {
      return `USD ${price}`;
    }
    return `${currency.code} ${price}`;
  }

  get descriptionLength(): number {
    return this.form.get('description')?.value?.length ?? 0;
  }

  get disabledReason(): string {
    if (!this.isEditing && this.bundleItems.length === 0) {
      return 'Debes agregar al menos un producto al paquete.';
    }

    if (!this.form.invalid) return '';

    const errorMap: { [key: string]: string } = {
      name: 'el nombre',
      sku: 'el SKU',
      description: 'la descripción',
      bundlePrice: 'el precio del paquete',
      discountPercent: 'el descuento',
      imageUrl: 'la URL de imagen',
    };

    for (const [field, label] of Object.entries(errorMap)) {
      const control = this.form.get(field);
      if (control?.invalid) {
        if (control.errors?.['required']) return `Falta completar ${label}.`;
        if (control.errors?.['maxlength']) {
          const max = control.errors['maxlength'].requiredLength;
          return `${label.charAt(0).toUpperCase() + label.slice(1)} supera el máximo de ${max} caracteres.`;
        }
        if (control.errors?.['min'])
          return `${label.charAt(0).toUpperCase() + label.slice(1)} no es válido.`;
        if (control.errors?.['max'])
          return `${label.charAt(0).toUpperCase() + label.slice(1)} no es válido.`;
      }
    }

    return 'Revisa los campos marcados en rojo.';
  }

  get precioCalculadoLocal(): number {
    if (!this.isSumOfComponents) return 0;

    return this.bundleItems.reduce((total, item) => {
      const precio = this.getProductPrice(item.idProduct) ?? 0;
      return total + precio * item.quantity;
    }, 0);
  }
}
